import { Button } from "@light-technology/components";
import { BatteryFull, Download01, Sun } from "@untitledui/icons";
import { useState } from "react";
import { toast } from "sonner";
import { Leaf01 } from "../../assets/leaf-01";
import { api } from "../../state/api";
import {
  type AppResponse,
  type PlanResponse,
  type ProgramBatteryDetails,
} from "../../state/api.types";
import { formatCents, formatCurrency } from "../../utilities/currency";
import { downloadFile } from "../../utilities/fileDownload";
import { planDetailsLink } from "../../utilities/links";
import { useBatteryProgram } from "../../utilities/programHelper";
import { PlanDesignElement } from "./PlanDesignElement";
import { PlanTypeIcon } from "./PlanTypeIcon";

type PlanDesignProps = {
  app: AppResponse;
  plan: PlanResponse;
};

type DownloadEFLsProps = {
  plan: PlanResponse;
};

function getBatteryCredit(battery_program_details: ProgramBatteryDetails) {
  return battery_program_details.monthly_credit_dollars
    ? formatCurrency(battery_program_details.monthly_credit_dollars) +
        " monthly"
    : formatCurrency(battery_program_details.monthly_credit_per_kwh_dollars) +
        " per kWh monthly";
}

export const PlanDesign = ({ app, plan }: PlanDesignProps) => {
  const isRenewable = plan.renewable_percent === 100;
  const battery_program_details = useBatteryProgram(plan)?.battery_details;

  return (
    <div className="rounded-xl border border-secondary p-3xl">
      <div className="flex flex-col gap-4xl">
        <div className="flex flex-col gap-2">
          <div className="flex flex-row justify-between">
            <div className="font-medium text-secondary">Plan design</div>
            <div className="flex flex-row gap-2">
              <PlanTypeIcon
                title="100% renewable energy"
                inactiveTitle="Does not include 100% renewable energy"
                isActive={isRenewable}
              >
                <Leaf01 className="max-h-6" />
              </PlanTypeIcon>
              <PlanTypeIcon
                title="Includes solar buyback"
                inactiveTitle="Does not include solar buyback"
                isActive={plan.has_solar_buyback ?? false}
              >
                <Sun className="max-h-6" />
              </PlanTypeIcon>
              <PlanTypeIcon
                title="Includes battery program"
                inactiveTitle="Does not includes battery program"
                isActive={plan.has_storage ?? false}
              >
                <BatteryFull className="max-h-6" />
              </PlanTypeIcon>
            </div>
          </div>
          {plan.description && (
            <div className="text-sm text-tertiary">{plan.description}</div>
          )}
        </div>
        <div className="flex flex-wrap gap-3xl">
          <PlanDesignElement
            title="Term length"
            value={`${plan.term_months.toString()} months`}
          />
          <PlanDesignElement
            title="Monthly base fee"
            value={formatCurrency(plan.plan_monthly_cost_dollars)}
          />
          <PlanDesignElement
            title="Monthly commission"
            value={formatCurrency(plan.partner_commission_per_month_dollars)}
          />
          <PlanDesignElement
            title="Renewal plan"
            value={<RenewalPlan app={app} plan={plan} />}
          />
          <PlanDesignElement
            title="Renewable content"
            value={`${plan.renewable_percent.toString()}%`}
          />
          {plan.has_solar_buyback && (
            <PlanDesignElement
              title="Solar buyback"
              value={
                plan.export_rate_kwh_dollars
                  ? `${formatCents(parseFloat(plan.export_rate_kwh_dollars) * 100)}¢ per kWh`
                  : "Matched to energy rate"
              }
            />
          )}
          {battery_program_details && (
            <PlanDesignElement
              title="Customer battery credit"
              value={getBatteryCredit(battery_program_details)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export const DownloadEFLs = ({ plan }: DownloadEFLsProps) => {
  const [trigger] = api.useLazyGetPlanEFLsQuery();
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    setIsDownloading(true);

    try {
      const result = await trigger(plan.uuid).unwrap();
      const url = URL.createObjectURL(result.blob);

      downloadFile(url, result.filename);
      URL.revokeObjectURL(url);
      toast.success("EFLs have been downloaded.");
    } catch {
      toast.error(
        "The EFLs could not be downloaded at this time. Please try again later.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      className="self-start"
      color="secondary"
      size="sm"
      isLoading={isDownloading}
      onClick={handleDownload}
      iconLeading={Download01}
    >
      Download EFLs
    </Button>
  );
};

const RenewalPlan = ({ app, plan }: PlanDesignProps) => {
  const renewalPlanUuid = plan.renewal_plan_uuid;
  const renewalPlanName = plan.renewal_plan_name;

  // If null then plan is its own renewal plan
  if (!renewalPlanUuid) {
    return <span>Current plan</span>;
  }

  return (
    <a href={planDetailsLink(app.slug, renewalPlanUuid)} className="underline">
      {renewalPlanName}
    </a>
  );
};
